import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@/lib/apiBase";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toastError, toastSuccess } from "@/lib/toast";

const RETENTION_OPTIONS = [7, 30, 90];
const DEFAULT_BY_KIND = { posts: 7, cards: 30, channels: 90 };

function pickDays(raw, kind) {
  const n = Number(raw);
  return RETENTION_OPTIONS.includes(n) ? n : DEFAULT_BY_KIND[kind];
}

/**
 * Org settings → Retention: soft-delete windows for posts, cards, channels.
 * Purge enforcement is future Suite Jobs work; this panel only stores policy.
 */
const OrgRetentionSettingsPanel = ({ orgId, accessToken, initialOrg }) => {
  const [postsDays, setPostsDays] = useState(
    pickDays(initialOrg?.retention_posts_days, "posts")
  );
  const [cardsDays, setCardsDays] = useState(
    pickDays(initialOrg?.retention_cards_days, "cards")
  );
  const [channelsDays, setChannelsDays] = useState(
    pickDays(initialOrg?.retention_channels_days, "channels")
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setPostsDays(pickDays(initialOrg?.retention_posts_days, "posts"));
    setCardsDays(pickDays(initialOrg?.retention_cards_days, "cards"));
    setChannelsDays(pickDays(initialOrg?.retention_channels_days, "channels"));
  }, [initialOrg]);

  const loadFromApi = useCallback(async () => {
    if (!orgId || !accessToken) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/org/${orgId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setPostsDays(pickDays(res.data?.retention_posts_days, "posts"));
      setCardsDays(pickDays(res.data?.retention_cards_days, "cards"));
      setChannelsDays(pickDays(res.data?.retention_channels_days, "channels"));
    } catch (err) {
      console.error(err);
    }
  }, [orgId, accessToken]);

  useEffect(() => {
    if (!initialOrg) loadFromApi();
  }, [initialOrg, loadFromApi]);

  const handleSave = async () => {
    if (!orgId || !accessToken) return;
    setBusy(true);
    setError(null);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/org/${orgId}/retention`,
        {
          posts_days: postsDays,
          cards_days: cardsDays,
          channels_days: channelsDays,
        },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setPostsDays(pickDays(res.data?.posts_days, "posts"));
      setCardsDays(pickDays(res.data?.cards_days, "cards"));
      setChannelsDays(pickDays(res.data?.channels_days, "channels"));
      toastSuccess("Retention policy saved");
    } catch (err) {
      console.error(err);
      setError(
        err.response?.data?.message || "Failed to save retention policy"
      );
      toastError(err, "Failed to save retention policy");
    } finally {
      setBusy(false);
    }
  };

  const renderSelect = (id, label, value, onChange, hint) => (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      {hint && (
        <p className="text-sm text-muted-foreground">{hint}</p>
      )}
      <select
        id={id}
        className="flex h-10 w-full max-w-xs rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={busy}
      >
        {RETENTION_OPTIONS.map((d) => (
          <option key={d} value={d}>
            {d} days
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h3 className="text-base font-semibold">Data retention</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Applies after soft-delete. Soft-deleted items remain in Archive until
          a future purge job removes them after this window. Direct messages are
          not covered.
        </p>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="space-y-6">
        {renderSelect(
          "retention-posts",
          "Posts",
          postsDays,
          setPostsDays,
          "Feed posts (not kanban cards)."
        )}
        {renderSelect(
          "retention-cards",
          "Cards",
          cardsDays,
          setCardsDays,
          "Kanban board cards."
        )}
        {renderSelect(
          "retention-channels",
          "Channels",
          channelsDays,
          setChannelsDays,
          "Team channels (not DMs)."
        )}
      </div>

      <Button onClick={handleSave} disabled={busy}>
        {busy ? "Saving…" : "Save retention policy"}
      </Button>
    </div>
  );
};

export default OrgRetentionSettingsPanel;
